/**
 * Promise-returning confirm dialog. Replaces `window.confirm` for cases
 * where we want a styled modal instead of the browser-native one.
 *
 * Usage:
 *   if (await confirm({ title: 'Discard changes?', danger: true })) ...
 *
 * A single mount-point in App renders the active dialog. Multiple
 * concurrent calls are queued — only the head shows; the others wait.
 *
 * Why this and not a hook? `confirmIfDirty` returns a callback that
 * *might* show a modal, and that callback can be passed deep into
 * components that don't have hook access (event handlers, refs). The
 * imperative singleton API hides the React side of things from the
 * caller.
 */

import { useEffect, useRef, useState } from 'react';

import { Icon } from './Icon.js';
import './ConfirmDialog.css';

/** Tone icon shown beside the dialog title. Danger uses the alert glyph. */
function ConfirmIcon({ danger }: { danger: boolean }): JSX.Element {
  return (
    <span className={'confirm-icon' + (danger ? ' confirm-icon-danger' : ' confirm-icon-info')}>
      <Icon name={danger ? 'alert-circle' : 'info-circle'} size={18} strokeWidth={2.2} />
    </span>
  );
}

export interface ConfirmOptions {
  /** Title shown at the top of the dialog. */
  title: string;
  /** Body text — what the user is about to do. */
  message?: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Style the confirm button as destructive. */
  danger?: boolean;
}

/**
 * Three-way confirm: primary / secondary / cancel. Used for "you're
 * leaving an in-progress draft — Apply, Discard, or Cancel" style
 * prompts where neither answer is a clear default. The middle option
 * is rendered between Cancel and Primary so the visual ordering is
 * Cancel | Secondary | Primary, matching OS dialog convention.
 */
export interface ThreeWayOptions {
  title: string;
  message?: string;
  /** Primary action (e.g. "Apply"). Rendered as primary button. */
  primaryLabel: string;
  /** Secondary action (e.g. "Discard"). Rendered as outlined button. */
  secondaryLabel: string;
  /** Cancel action label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Style the secondary button as destructive (typical for "Discard"). */
  secondaryDanger?: boolean;
}

export type ThreeWayChoice = 'primary' | 'secondary' | 'cancel';

interface QueueEntry {
  // Discriminator for the renderer.
  kind: 'binary' | 'threeway';
  options: ConfirmOptions | ThreeWayOptions;
  resolve: (result: boolean | ThreeWayChoice) => void;
}

type Listener = (entry: QueueEntry | null) => void;

const queue: QueueEntry[] = [];
const listeners = new Set<Listener>();

function notify(): void {
  const head = queue[0] ?? null;
  for (const l of listeners) l(head);
}

export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    queue.push({
      kind: 'binary',
      options,
      resolve: resolve as (r: boolean | ThreeWayChoice) => void,
    });
    notify();
  });
}

/**
 * Three-way prompt: primary / secondary / cancel. See `ThreeWayOptions`.
 */
export function confirmThreeWay(options: ThreeWayOptions): Promise<ThreeWayChoice> {
  return new Promise<ThreeWayChoice>((resolve) => {
    queue.push({
      kind: 'threeway',
      options,
      resolve: resolve as (r: boolean | ThreeWayChoice) => void,
    });
    notify();
  });
}

function dismissBinary(ok: boolean): void {
  const head = queue.shift();
  if (head?.kind === 'binary') head.resolve(ok);
  notify();
}

function dismissThreeWay(choice: ThreeWayChoice): void {
  const head = queue.shift();
  if (head?.kind === 'threeway') head.resolve(choice);
  notify();
}

/**
 * Mount once at the app root. Renders whichever dialog is at the head
 * of the queue.
 */
export function ConfirmDialogHost(): JSX.Element | null {
  const [entry, setEntry] = useState<QueueEntry | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const listener: Listener = (head) => setEntry(head);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Open / close in sync with `entry`. Use showModal for the focus
  // trap + Escape handling.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (entry && !d.open) {
      d.showModal();
      // Default focus on Cancel — matches OS dialogs and avoids Enter
      // accidentally confirming destructive actions.
      window.setTimeout(() => cancelRef.current?.focus(), 0);
    } else if (!entry && d.open) {
      d.close();
    }
  }, [entry]);

  // Native dialog Esc dismissal needs to feed back into our queue.
  // Esc behaves like Cancel for both kinds.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const onClose = (): void => {
      const head = queue[0];
      if (!head) return;
      if (head.kind === 'binary') dismissBinary(false);
      else dismissThreeWay('cancel');
    };
    d.addEventListener('cancel', onClose);
    return () => d.removeEventListener('cancel', onClose);
  }, []);

  if (!entry) return null;
  const { options } = entry;
  if (entry.kind === 'threeway') {
    const opts = options as ThreeWayOptions;
    return (
      <dialog ref={dialogRef} className="confirm-dialog" aria-labelledby="confirm-title">
        <div className="confirm-header">
          <ConfirmIcon danger={opts.secondaryDanger ?? false} />
          <h2 id="confirm-title" className="confirm-title">
            {opts.title}
          </h2>
        </div>
        {opts.message && <p className="confirm-message">{opts.message}</p>}
        <footer className="confirm-footer">
          <button
            ref={cancelRef}
            type="button"
            className="btn-secondary"
            onClick={() => dismissThreeWay('cancel')}
          >
            {opts.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            className={opts.secondaryDanger ? 'btn-danger' : 'btn-secondary'}
            onClick={() => dismissThreeWay('secondary')}
          >
            {opts.secondaryLabel}
          </button>
          <button type="button" className="btn-primary" onClick={() => dismissThreeWay('primary')}>
            {opts.primaryLabel}
          </button>
        </footer>
      </dialog>
    );
  }
  const binary = options as ConfirmOptions;
  return (
    <dialog ref={dialogRef} className="confirm-dialog" aria-labelledby="confirm-title">
      <div className="confirm-header">
        <ConfirmIcon danger={binary.danger ?? false} />
        <h2 id="confirm-title" className="confirm-title">
          {binary.title}
        </h2>
      </div>
      {binary.message && <p className="confirm-message">{binary.message}</p>}
      <footer className="confirm-footer">
        <button
          ref={cancelRef}
          type="button"
          className="btn-secondary"
          onClick={() => dismissBinary(false)}
        >
          {binary.cancelLabel ?? 'Cancel'}
        </button>
        <button
          type="button"
          className={binary.danger ? 'btn-danger' : 'btn-primary'}
          onClick={() => dismissBinary(true)}
        >
          {binary.confirmLabel ?? 'Confirm'}
        </button>
      </footer>
    </dialog>
  );
}
